import { Grid } from '@bedrock-layout/primitives'
import { ArticleCard, Button, Heading, Img, LottiePlayer } from '@okam/brand-ui/index'
import { safeTruncate } from '@okam/core-lib'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React from 'react'
import tw, { css } from 'twin.macro'
import { toDirectusUrl } from '@/lib/directus'
import { QueryKeys, useArticles } from '@/lib/react-query'
import useLottie from '@/lib/react-query/useLottie'
import withForwardedRefSection from '@/lib/utils/withForwardedRefSection'
import { useFragment } from 'gql'
import { blockContentFragment, colorSchemeFragment } from 'query/single-page/fragment'
import { ARTICLES_LIMIT_LCDH_PAGE } from '../../../../../const'
import type { TPetParentsCornerContentBlock } from '../../types'

const ArticlesPetParentsCornerBlock = (props: TPetParentsCornerContentBlock) => {
  const { title, colors } = useFragment(blockContentFragment, props)

  const { data: lottieData } = useLottie('recent-articles-lottie', 'Illust. chien small-2.json' ?? '')

  const { locale } = useRouter()

  const { data: articlesData, isSuccess: articlesSuccess } = useArticles(
    QueryKeys.GetPetParentsCornerArticles,
    locale ?? 'fr',
    ARTICLES_LIMIT_LCDH_PAGE,
  )

  const { t } = useTranslation('common')

  const blockColors = useFragment(colorSchemeFragment, colors)

  const articlesLink = t('URL.ARTICLES')
  const articlesLinkText = t('BUTTON.SEE_ALL')

  return (
    <div
      css={[
        css`
          background-color: ${blockColors?.background};
          ${tw`flex relative flex-col items-start font-semibold pb-32 pt-48 overflow-hidden px-6 md:px-20`}
        `,
      ]}
    >
      <div tw="absolute right-6 -top-20 sm:-top-6 z-[0] max-w-[24rem] sm:max-w-[unset]">
        <LottiePlayer
          src={toDirectusUrl(lottieData?.[0].lottie?.id, lottieData?.[0]?.lottie?.filename) ?? ''}
          autoplay
          loop
          controls={false}
          hover={false}
          direction={1}
          keepLastFrame={false}
          speed={1}
          width="75%"
        />
      </div>
      <Heading as="h2" variant="h2" tw="pb-4 relative z-[2]">
        {title}
      </Heading>
      <Button tw="pb-10" href={articlesLink} as="a" buttonStyle="hollow" icon="ArrowRight" variant="textIcon">
        {articlesLinkText}
      </Button>
      <Grid
        gutter="2rem"
        minItemWidth={300}
        css={css`
          grid-template-columns: repeat(auto-fit, minmax(max(18rem, 100%/5), 1fr)) !important;
          ${tw`w-full`}
        `}
      >
        {articlesSuccess &&
          articlesData?.articles?.map((article) => {
            const image = article?.translations?.[0]?.featuredImage
              ? article?.translations?.[0]?.featuredImage
              : article?.featuredImage
            const translation = article?.translations?.[0]
            return (
              <ArticleCard
                key={article.id}
                bgColor="transparent"
                link={t('URL.SINGLE_ARTICLE', { slug: translation?.slug ?? '' })}
                text={translation?.content ?? ''}
                title={safeTruncate(translation?.title ?? '', 100)}
                excerpt={translation?.excerpt}
                styles={css`
                  ${tw`!mx-0`}
                `}
              >
                <Img
                  css={css`
                    aspect-ratio: 1/1;
                    object-fit: cover;
                  `}
                  id={image?.id}
                  filename={image?.filenameDownload}
                  alt={image?.description ?? ''}
                  width={image?.width ?? 500}
                  height={image?.height ?? 500}
                />
              </ArticleCard>
            )
          })}
      </Grid>
    </div>
  )
}

export default withForwardedRefSection(ArticlesPetParentsCornerBlock)
